import React, { useContext, useEffect, useState } from 'react'
import Image from 'react-bootstrap/Image'
import { modalContext } from '../../modules/shared'
import { getUpdateModalCallback } from '../modal'
import { useExport } from '../../modules/reports/contexts/export-context'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { reportContext } from 'app/modules/reports/contexts'
import { useReport } from 'app/modules/reports/hooks'
import IconButton from '../icon-button'

type ZoomableThumbnailItem = {
  mediaType: string
  base64: string
}

type ZoomableThumbnailParams = {
  items: ZoomableThumbnailItem[]
  activeItemIdx?: number
  title?: string
  modalClass?: string
  otherContent?: { [key: string]: any }
  className?: string
  textLink?: string
  variant?: 'file-preview' | 'url-details'
}
export function ZoomableThumbnail({
  items,
  activeItemIdx = 0,
  title,
  modalClass,
  otherContent,
  className,
  textLink,
  variant,
}: ZoomableThumbnailParams): JSX.Element {
  const { _t } = useTypedTranslation()
  const { exporting } = useExport()
  const modalWrapper = useContext(modalContext)
  const hideModal = getUpdateModalCallback(modalWrapper, { show: false })
  const showModal = getUpdateModalCallback(modalWrapper, {
    show: true,
    title: title || _t('image-preview'),
    body: <ModalBody items={items} activeItemIdx={activeItemIdx} otherContent={otherContent} />,
    props: {
      dialogClassName: 'modal-size-auto ' + (modalClass ?? ''),
      onHide: hideModal,
    },
  })

  const allowModal = !exporting && !!items[activeItemIdx].base64
  const fullClassName = (allowModal ? '' : 'disabled h-100 w-100 cursor-default') + (className ?? '')

  if (textLink && allowModal) {
    return (
      <a
        href="#"
        className={'lonely-link ' + (className ?? '')}
        onClick={(e) => {
          e.preventDefault()
          showModal()
        }}
      >
        {textLink}
      </a>
    )
  }

  return (
    <button
      type="button"
      style={{ minHeight: '150px' }}
      className={`unstyled-button ${fullClassName}`}
      onClick={allowModal ? showModal : undefined}
    >
      <ThumbItem item={items[activeItemIdx]} variant={variant} />
    </button>
  )
}

function ThumbItem({ item, variant }: { item: ZoomableThumbnailItem; variant?: 'file-preview' | 'url-details' }) {
  const reportsWrapper = useContext(reportContext)
  const { reportId } = useReport()
  const filesDownloadFinished = reportsWrapper.getReportFilesLoadingState(reportId)
  const reportOverview = reportsWrapper.getOverview(reportId)
  const { _t } = useTypedTranslation()

  const imageExists = !!item.base64
  const imageInProgress = !imageExists && !filesDownloadFinished

  const placeholderClass =
    (variant === 'file-preview'
      ? 'file-preview-thumbnail file-preview-placeholder'
      : 'url-details-preview-placeholder') + ' pe-none font-size-13'

  if (imageInProgress) {
    return (
      <div className={placeholderClass}>
        <span className="ds3-icon ds3-process" />
        {_t('file-status-not-ready')}
      </div>
    )
  }

  if (!imageExists && reportOverview.private) {
    return (
      <div className={placeholderClass}>
        <span className="ds3-icon ds3-lock" />
        {_t('file-status-private')}
      </div>
    )
  }

  if (!imageExists) {
    return (
      <div className={placeholderClass}>
        <span className="ds3-icon ds3-analysis" />
        {_t('file-status-not-found')}
      </div>
    )
  }

  return <Image src={`data:${item.mediaType};base64,${item.base64}`} className="report-thumbnail" alt="preview" />
}

function ModalBody({
  items,
  activeItemIdx,
  otherContent,
}: {
  items: ZoomableThumbnailItem[]
  activeItemIdx: number
  otherContent?: { [key: string]: any }
}) {
  return (
    <>
      <ModalImage items={items} activeItemIdx={activeItemIdx} />
      {otherContent?.afterImage ? otherContent.afterImage : null}
    </>
  )
}

/**
 * Build image modal content.
 * It is either a single image or a slideshow component.
 */
function ModalImage({ items, activeItemIdx }: { items: ZoomableThumbnailItem[]; activeItemIdx: number }) {
  const [sliderImageIdx, setSliderImageIdx] = useState(activeItemIdx)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return
      }
      const delta = event.key === 'ArrowLeft' ? -1 : 1
      moveIdx(delta)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [items])

  if (items.length === 1) {
    return <Image src={`data:${items[0].mediaType};base64,${items[0].base64}`} alt="zoomed-preview" />
  }

  if (items.length === 1) {
    return <Image src={`data:${items[0].mediaType};base64,${items[0].base64}`} alt="zoomed-preview" />
  }

  if (typeof sliderImageIdx === 'undefined' || !items[sliderImageIdx]) {
    return null
  }

  const moveIdx = (move: number) => {
    setSliderImageIdx((prev) => {
      const curr = prev + move
      if (curr < 0 || curr >= items.length) {
        return prev
      }
      return curr
    })
  }

  const item = items[sliderImageIdx]
  const leftArrowClass = sliderImageIdx > 0 ? '' : 'invisible'
  const rightArrowClass = sliderImageIdx < items.length - 1 ? '' : 'invisible'

  return (
    <div className="slider-container">
      <IconButton iconClass={`fas fa-chevron-left slider-button ${leftArrowClass}`} onClick={() => moveIdx(-1)} />
      <div>
        <Image src={`data:${item.mediaType};base64,${item.base64}`} alt="zoomed-preview" />
      </div>
      <IconButton iconClass={`fas fa-chevron-right slider-button ${rightArrowClass}`} onClick={() => moveIdx(1)} />
    </div>
  )
}
